// Bell popover shown in the Topbar. Consumes unread-count + recent list.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api, extractErrorMessage } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import NotificationItem from "./NotificationItem";

function formatCount(n) {
  if (!n) return "0";
  if (n > 99) return "99+";
  return String(n);
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: count = 0 } = useQuery({
    queryKey: ["unread-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data.count,
    refetchInterval: 30000,
    staleTime: 20000,
  });

  const { data: list, isLoading } = useQuery({
    queryKey: ["notifications", "popover"],
    queryFn: async () => (await api.get("/notifications", { params: { limit: 10 } })).data,
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["unread-count"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post("/notifications/mark-all-read", {}),
    onSuccess: (res) => {
      toast.success(`${res.data.updated} notification(s) marquée(s) comme lue(s)`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["unread-count"] });
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Échec")),
  });

  const handleClick = (notif) => {
    if (!notif.read_at) markRead.mutate(notif.id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="notifications-btn"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <Badge
              data-testid="notifications-badge"
              /* BUG-010 : blanc sur red-500 = 3,76:1 à 10 px. red-600 : 4,83:1. */
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] bg-red-600 hover:bg-red-600 text-white rounded-full"
            >
              {formatCount(count)}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notifications-popover"
        align="end"
        className="w-96 p-0"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm font-semibold text-foreground">Notifications</p>
          {count > 0 && (
            <Button
              data-testid="mark-all-read-btn-popover"
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-primary"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Tout marquer comme lu
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className="max-h-96">
          <div className="p-2">
            {isLoading && (
              <p className="text-xs text-muted-foreground px-3 py-6 text-center">
                Chargement…
              </p>
            )}
            {!isLoading && list && list.items.length === 0 && (
              <p
                data-testid="notifications-empty"
                className="text-xs text-muted-foreground px-3 py-10 text-center"
              >
                Aucune notification pour le moment
              </p>
            )}
            {list?.items?.map((n) => (
              <NotificationItem
                key={n.id}
                notif={n}
                onClick={handleClick}
                compact
              />
            ))}
          </div>
        </ScrollArea>
        <Separator />
        <div className="px-4 py-2.5">
          <Button
            data-testid="see-all-notifications-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs text-primary hover:text-primary"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
          >
            Voir toutes les notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
